<template>
  <div class="page">
    <div
      v-if="!$config.isSaas && routeFrom && routeFrom.name === 'SphereDetail'"
      class="back-link"
      @click="$router.push(routeFrom)"
    >
      <b-icon-chevron-left font-scale="1.3" />
      <span>
        {{ $t('words.organisationgroup') }}
      </span>
    </div>
    <div
      v-else-if="!$config.isSaas && accessOrganisationsList"
      class="back-link"
      @click="$router.push({ name: 'Organisations' })"
    >
      <b-icon-chevron-left font-scale="1.3" />
      <span>
        {{ $t('organisationDetail.organisationsList') }}
      </span>
    </div>
    <div class="container-xxl">
      <b-overlay
        no-wrap
        :show="loading"
        :variant="'white'"
        :opacity="0.8"
        :blur="'2px'"
        rounded="sm"
      />
      <ValidationObserver v-slot="{ handleSubmit }">
        <b-row>
          <b-col
            cols="8"
            class="detail-forms"
          >
            <h1 style="margin-left: 1rem;">
              {{ currentOrganisation.value ? currentOrganisation.value.display_name : $tc('words.organisation', 1) }}
            </h1>
            <b-alert
              v-if="currentUsergroupMembersAwaitingValidation > 0"
              variant="warning"
              show
              style="margin: 0 0 1rem 1rem;"
            >
              Vous avez <b>{{ currentUsergroupMembersAwaitingValidation }}</b> utilisateurs en attente d'activation. 
              Retrouvez ces utilisateurs dans la table des membres en bas de page.
            </b-alert>
            <OrganisationQuota
              v-if="$config.isSaas && currentOrganisationQuota"
              :quota="currentOrganisationQuota"
            />
            <OrganisationForm
              :organisation="currentOrganisation.value"
              @update="updateFormData"
            />
          </b-col>
          <b-col cols="4">
            <div class="card-footer">
              <UsergroupThumbnail
                v-if="currentOrganisation.value"
                :display-name="currentOrganisation.value.display_name"
                :organisation="currentOrganisation.value"
                :usergroup="currentUsergroup"
                :thumbnail="currentOrganisation.value.thumbnail"
              />
              <div
                class="side-footer"
                style="margin-bottom: 0;"
              >
                <b-button
                  variant="outline-secondary"
                  style="margin-bottom: 0;"
                  @click="handleSubmit(saveOrganisation)"
                >
                  {{ $t('buttons.saveChanges') }}
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </ValidationObserver>
      <b-row
        v-if="currentOrganisation.value"
        style="height: auto;"
      >
        <b-col
          cols="11"
          class="users-table"
          style="margin-left: 1rem;"
        >
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h3 class="line-header">
              <img
                src="@/assets/icons/face_user.svg"
                alt="Icon face user"
              > {{ capitalizeFirstLetter($tc('words.user', 2)) }}
            </h3>
            <b-button
              
              variant="primary"
              @click="openAddMemberForm"
            >
              <b-icon-plus />
              {{ $t('addUser.title') }}
            </b-button>
          </div>
          <div
            v-if="isAddingMember"
            class="ml-3"
          >
            <ValidationObserver v-slot="{ handleSubmit }">
              <OrganisationUserForm
                :user="newMember"
                @change="handleNewMemberChange"
                @delete="cancelAddMember"
                @add="handleSubmit(addMember)"
              />
            </ValidationObserver>
          </div>
          <OrganisationMembersTable
            :organisation="currentOrganisation.value"
            style="margin: 1rem 0 0 1rem;"
            @update:loading="loading = $event"
            @refresh="refreshOrganisationView({
              members: true
            })"
          />
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col
          cols="11"
          class="contacts-table"
          style="margin-left: 1rem;"
        >
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h3 class="line-header">
              <img
                src="@/assets/icons/user-group.svg"
                alt="Icône face user"
              > Contacts
            </h3>
            <b-button
              v-if="!isCreatingContact"
              variant="primary"
              @click.prevent="isCreatingContact = true"
            >
              <b-icon-plus />
              Créer un contact
            </b-button>
          </div>
          <b-overlay
            :show="loadingContactsTable"
            :variant="'white'"
            :opacity="0.9"
            :blur="'2px'"
            rounded="sm"
          >
            <div style="margin-left: 1rem;">
              <div v-if="isCreatingContact">
                <ValidationObserver v-slot="{ handleSubmit }">
                  <DatasetContactForm
                    :current-contacts="contactsList"
                    :contact="newContact"
                    :usergroup-disabled="true"
                    @change="setNewContact"
                    @delete="isCreatingContact = false"
                    @create="handleSubmit(addContact)"
                  />
                </ValidationObserver>
              </div>
              <SearchablePaginatedTable
                :name="'organisationContactsTable'"
                :margin="'0 0 1rem 0'"
                :searchable="true"
                :search-placeholder="$t('organisationDetail.contactsTable.search')"
                :search-function="SEARCH_CONTACTS_LIST"
                :fields="contactsFields"
                :rows="contactsRows"
                :count="contactsCount"
                :nb-pages="Math.ceil(contactsCount / $config.itemsPerPage.organisationContacts) || 1"
                :default-sorting="{
                  sortedUp: true,
                  sortedDown: false,
                  sortedField: 'name'
                }"
                :sort-function="GET_CONTACTS_LIST"
                :selectable="false"
                :allow-modification="true"
                @change-page="changeContactsTablePage"
                @sorted="setMembersTableSorted"
                @modify="modifyContact"
                @delete="removeContact"
              />
            </div>
          </b-overlay>
        </b-col>
      </b-row>

      <b-row v-if="!$config.isSaas">
        <b-col
          cols="11"
          class="users-table"
          style="margin-left: 1rem;"
        >
          <h3 class="line-header">
            <img
              src="@/assets/icons/sphere_planet.svg"
              alt="Icon sphere planet"
            > {{ capitalizeFirstLetter($tc('words.organisationGroup', 2)) }}
          </h3>
          <div style="margin-left: 1em;">
            <label for="search-organisation-usergroups">
              {{ $t('organisationDetail.addSphereLabel') }}
            </label>
            <p>
              {{ $t('organisationDetail.addSphereHelp') }}
            </p>
            <SearchUsergroups
              id="search-organisation-usergroups"
              :placeholder="$t('searchbars.usergroupPlaceholder')"
              :types="['group-of-organisation']"
              :limit="100"
              @select="addOrgToSphere"
            />
            <div
              v-if="selectedSpheres.length > 0"
              id="orga-spheres-container"
            >
              <div
                v-for="selectedSphere of selectedSpheres"
                :key="selectedSphere.id"
                class="orga-sphere"
              >
                {{ selectedSphere.display_name }}
                <b-icon-x
                  font-scale="1.5"
                  @click="removeOrgFromSphere(selectedSphere)"
                />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row v-if="!$config.isSaas">
        <b-col
          cols="11"
          class="users-table"
          style="margin-left: 1rem;"
        >
          <h3 class="line-header">
            <img
              src="@/assets/icons/cloud_download.svg"
              alt="Icon sphere planet"
            > {{ capitalizeFirstLetter($tc('words.indexer', 2)) }}
          </h3>
          <div style="margin-left: 1em;">
            <b-overlay
              :show="loadingIndexers"
              :variant="'white'"
              :opacity="0.9"
              :blur="'2px'"
              rounded="sm"
            >
              <button
                class="add-button"
                style="margin-bottom: 1rem;"
                @click.prevent="$router.push({
                  name: 'AddIndexer',
                  params: {
                    usergroupId: currentOrganisation.value.usergroup.id
                  }
                })"
              >
                <b-icon-plus />
                {{ capitalizeFirstLetter($tc('indexers.add')) }}
              </button>
              <IndexersTable
                v-if="
                  currentOrganisation.value &&
                    currentOrganisation.value.usergroup &&
                    currentOrganisation.value.usergroup.id
                "
                :key="currentOrganisation.value.usergroup.id"
                :usergroup-id="currentOrganisation.value.usergroup.id"
                @loading="$event => loadingIndexers = $event"
              />
            </b-overlay>
          </div>
        </b-col>
      </b-row>
    </div>
    <UpdateContact
      :open="isUpdatingContact"
      :contacts-list="contactsList"
      :contact="contactToUpdate"
      @update="updateContact"
      @hide="isUpdatingContact = false"
    />
  </div>
</template>

<script>
import OrganisationForm from '@/app/organisation/components/OrganisationForm.vue';
import SearchablePaginatedTable from '@/components/SearchablePaginatedTable/Layout.vue';
import DatasetContactForm from '@/app/dataset/components/DatasetContactForm.vue';
import IndexersTable from '@/app/indexer/components/IndexersTable.vue';
import UpdateContact from '@/components/Modals/UpdateContact.vue';
import SearchUsergroups from '@/components/Searchbars/SearchUsergroups.vue';
import UsergroupThumbnail from '@/components/Usergroup/UsergroupThumbnail.vue';
import OrganisationMembersTable from '@/app/organisation/components/OrganisationMembersTable.vue';
import OrganisationQuota from '@/app/organisation/components/OrganisationQuota.vue';
import OrganisationUserForm from '../components/OrganisationUserForm.vue';

import i18n from '@/common/i18n/i18n.js';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import { usernamify } from '@/utils';

import {
  datasetAPI,
  usersAPI,
  usergroupAPI,
} from '@/api';

import { ErrorService } from '@/services/error-service.js';
import { currentOrganisationMixin } from '@/app/organisation/mixins/currentOrganisation.mixin.js';

export default {
  name: 'OrganisationDetail',

  components: {
    OrganisationForm,
    SearchablePaginatedTable,
    SearchUsergroups,
    DatasetContactForm,
    IndexersTable,
    UpdateContact,
    UsergroupThumbnail,
    OrganisationMembersTable,
    OrganisationQuota,
    OrganisationUserForm,
    ValidationObserver,
  },

  mixins: [currentOrganisationMixin],

  data() {
    return {
      cardKey: 1,
      routeFrom: null,
      loading: false,

      // form
      formData: {},

      // tables
      loadingMembersTable: false,
      loadingContactsTable: false,
      sphere: null,
      selectedSpheres: [],

      membersTableSorted: {
        direction: '',
        field: 'user__last_name'
      },

      // members
      isAddingMember: false,
      newMember: {
        firstname: null,
        lastname: null,
        email: null,
        role: null
      },
      membersRows: [],

      // contact
      isCreatingContact: false,
      newContact: {
        id: null,
        role: null,
        contact: {
          id: null,
          email: null,
          name: '',
          position: null,
          phone_number: null,
          usergroup: null
        }
      },
      isUpdatingContact: false,
      contactToUpdate: null,

      // indexers
      loadingIndexers: false
    };
  },

  computed: {
    ...mapState('users', ['usersList']),
    ...mapState('organisations', [
      'organisationError'
    ]),
    ...mapState('usergroups', [
      'currentUsergroup',
      'usergroupsRoles',
      'currentUsergroupMembersCount',
      'currentUsergroupParents',
      'spheres',
      'contactsCount',
      'contactsList'
    ]),
    ...mapState('table', [
      'currentTables'
    ]),
    ...mapState('jurisdiction', [
      'jurisdictionsList'
    ]),
    ...mapGetters('usergroups', [
      'currentUsergroupMembersAwaitingValidation'
    ]),
     accessOrganisationsList() {
      return this.userSession.isSuperuser()||this.userSession.isDelegatedAdminMultipleOrganisations();
    },
    contactsFields() {
      return [
        {
          key: this.$t('organisationDetail.contactsTable.name'),
          apiName: 'name',
          sortable: false,
          width: '30',
          centered: false
        },
        {
          key: this.$t('organisationDetail.contactsTable.email'),
          apiName: 'email',
          sortable: false,
          width: '30%',
          centered: false
        },
        {
          key: this.$t('organisationDetail.contactsTable.phone'),
          apiName: 'phone_number',
          sortable: false,
          width: '15%',
          centered: false
        },
        {
          key: this.$t('organisationDetail.contactsTable.position'),
          apiName: 'role',
          sortable: false,
          width: '15',
          centered: false
        },
        {
          key: this.$t('organisationDetail.contactsTable.modify'),
          sortable: false,
          width: '10%',
          centered: true
        },
        {
          key: this.$t('organisationDetail.contactsTable.remove'),
          sortable: false,
          width: '10%',
          centered: true
        }
      ];
    },
    contactsRows() {
      return this.contactsList.map(el => {
        return {
          id: el.id,
          Nom: el.name,
          Email: el.email,
          Téléphone: el.phone_number,
          'Position/service': el.position
        };
      });
    }
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.routeFrom = from;
    });
  },

  async created() {
    try {
      this.loading = true;
      await this.fetchStaticData();
      await this.refreshOrganisationView({ fetchAll: true });
      this.cardKey += 1;
      this.newContact.contact.usergroup = this.currentOrganisation.value.usergroup.id;
    } catch (err) {
      console.error(err);
      this.loading = false;
    }
    this.SET_TABLE({
      name: 'organisationContactsTable',
      table: {
        loading: false,
        currentPage: 1,
        isTableSearched: false,
        searchQuery: null
      }
    });
  },

  mounted() {
    // Watch modal closing event
    this.$root.$on('bv::modal::hide', () => {
      this.isUpdatingContact = false;
    });
  },

  beforeRouteLeave(to, from, next) {
    this.SET_CURRENT_ORGANISATION({});
    this.currentOrganisation.value = null;
    next();
  },

  methods: {
    ...mapMutations('table', [
      'SET_TABLE'
    ]),
    ...mapMutations('organisations', [
      'SET_CURRENT_ORGANISATION'
    ]),
    ...mapActions('organisations', [
      'GET_ORGANISATION_DETAIL',
      'GET_ORGANISATIONS_LIST',
      'UPDATE_ORGANISATION',
      'SET_ORGANISATION_AGREEMENT',
    ]),
    ...mapActions('users', [
      'GET_USERS_LIST'
    ]),
    ...mapActions('usergroups', [
      'GET_USERGROUP_DETAIL',
      'GET_USERGROUPS_ROLES',
      'GET_USERGROUP_MEMBERS',
      'GET_USERGROUP_PARENTS',
      'GET_USERGROUPS_SPHERES_LIST',
      'GET_CONTACTS_LIST',
      'SEARCH_CONTACTS_LIST'
    ]),
    ...mapActions('jurisdiction', [
      'GET_JURISDICTIONS_LIST'
    ]),

    async fetchStaticData() {
      this.loading = true;
      try {
        if (this.jurisdictionsList.length === 0) {
          await this.GET_JURISDICTIONS_LIST();
        }
        if (this.usergroupsRoles.length === 0) {
          await this.GET_USERGROUPS_ROLES();
        }
        if (this.usersList.length === 0) {
          await this.GET_USERS_LIST({
            direction: null,
            field: null
          });
        }
        if (this.spheres.length === 0) {
          await this.GET_USERGROUPS_SPHERES_LIST({
            direction: null,
            field: null,
            page: 1
          });
        }
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    },

    async refreshOrganisationView(options = {}) {
      const fetchAll = options.fetchAll === true;
      const source = fetchAll
        ? { organisation: true, usergroup: true, members: true, parents: true, contacts: true }
        : options;
      const {
        organisation = false,
        usergroup = false,
        members = false,
        parents = false,
        contacts = false
      } = source;
      this.loading = true;
      try {
        if (organisation) {
          await this.GET_ORGANISATION_DETAIL(this.$route.params.id);
        }
        await Promise.all([
          ...usergroup ? [this.GET_USERGROUP_DETAIL(this.currentOrganisation.value.usergroup.id)] : [],
          ...members ? [this.GET_USERGROUP_MEMBERS({
            id: this.currentOrganisation.value.usergroup.id,
            ...this.membersTableSorted
          })] : [],
          ...parents ? [this.GET_USERGROUP_PARENTS({
            id: this.currentOrganisation.value.usergroup.id
          })] : [],
          ...contacts ? [this.GET_CONTACTS_LIST({
            usergroupId: this.currentOrganisation.value.usergroup.id
          })] : [],
        ]);
        if (parents) {
          for (const parent of this.currentUsergroupParents) {
            this.selectedSpheres.push(parent);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    updateFormData(data) {
      this.formData = data;
    },

    handleNewMemberChange(e) {
      this.newMember = e;
    },

    resetNewMember() {
      this.newMember = {
        firstname: null,
        lastname: null,
        email: null,
        role: null,
      };
    },

    openAddMemberForm() {
      this.resetNewMember();
      this.isAddingMember = true;
    },

    cancelAddMember() {
      this.isAddingMember = false;
      this.resetNewMember();
    },

    async addMember() {
      this.loading = true;
      let userId;
      const fullName = `${this.newMember.firstname || ''} ${this.newMember.lastname || ''}`.trim();
      try {
        userId = await usersAPI.searchUser(this.newMember.email);
      } catch (error) {
        if (error && error.response && error.response.status !== 404) {
          console.error(error);
        }
      }
      try {
        if (userId) {
          if (this.currentUsergroupMembers.value.some(el => el.user.id === userId)) {
            ErrorService.onError(
              null,
              i18n.messages[i18n.locale].messages.error.userAlreadyAddedToOrganisation.replace('{user}', fullName)
            );
            return;
          }
          await usergroupAPI.addUsergroupMember(
            this.currentOrganisation.value.usergroup.id,
            {
              user_id: userId,
              role: this.newMember.role,
              status: 2
            }
          );
        } else {
          try {
            let data = {
              username: usernamify(this.newMember.firstname.charAt(0).concat('', this.newMember.lastname)),
              first_name: this.newMember.firstname,
              last_name: this.newMember.lastname,
              email: this.newMember.email,
              role: this.newMember.role,
              usergroup_roles:[{
                usergroup_id: this.currentOrganisation.value.usergroup.id ,
                role: this.newMember.role,status:2
              }]
            };
            //create user 
            const newUser = await usersAPI.createUser(data);
            if (newUser.id) {
              ErrorService.onSuccess(
                null,
                i18n.messages[i18n.locale].messages.success.user.added.replace('{user}', fullName)
              );
            }
          } catch (error) {
            ErrorService.onError(error, i18n.messages[i18n.locale].messages.error.user.added.replace('{user}', fullName));
          }
        }
        await this.GET_USERGROUP_MEMBERS({
          id: this.currentOrganisation.value.usergroup.id,
          ...this.membersTableSorted
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isAddingMember = false;
        this.resetNewMember();
        this.loading = false;
      }
    },

    modifyContact(e) {
      this.contactToUpdate = {
        contact: this.contactsList.find(el => el.id === e.id)
      };
      this.isUpdatingContact = true;
    },

    async updateContact(e) {
      try {
        this.loadingContactsTable = true;
        await datasetAPI.updateContact(e.id, e);
        this.GET_CONTACTS_LIST({
          usergroupId: this.currentOrganisation.value.usergroup.id,
          page: this.currentTables.some(t => t.name === 'organisationContactsTable') ?
            this.currentTables.find(t => t.name === 'organisationContactsTable').currentPage :
            1
        })
          .then(() => {
            this.loadingContactsTable = false;
          })
          .catch(() => {
            this.loadingContactsTable = false;
          });
      } catch (err) {
        ErrorService.onError(err);
        this.loadingContactsTable = false;
        console.error(err);
      }
    },
    
    setNewContact(e) {
      this.newContact.contact = {
        position: e.contact.position,
        email: e.contact.email,
        name: e.contact.name,
        phone_number: e.contact.phone_number,
        usergroup_id: this.currentOrganisation.value.usergroup.id
      };
    },

    async addContact() {
      try {
        this.loadingContactsTable = true;
        await datasetAPI.createContact(this.newContact.contact);
        this.isCreatingContact = false;
        await this.GET_CONTACTS_LIST({
          usergroupId: this.currentOrganisation.value.usergroup.id,
          page: this.currentTables.some(t => t.name === 'organisationContactsTable') ?
            this.currentTables.find(t => t.name === 'organisationContactsTable').currentPage :
            1
        });
        this.newContact = {
          id: null,
          role: null,
          contact: {
            id: null,
            email: null,
            name: '',
            position: null,
            phone_number: null,
            usergroup: null
          }
        };
        this.loadingContactsTable = false;
      } catch (err) {
        ErrorService.onError(err);
        this.loadingContactsTable = false;
      }
    },

    async removeContact(e) {
      this.loadingContactsTable = true;
      await datasetAPI.removeContact(e.id);
      await this.GET_CONTACTS_LIST({
        usergroupId: this.currentOrganisation.value.usergroup.id,
        page: this.currentTables.some(t => t.name === 'organisationContactsTable') ?
          this.currentTables.find(t => t.name === 'organisationContactsTable').currentPage :
          1
      });
      this.loadingContactsTable = false;
    },

    saveOrganisation() {
      this.loading = true;
      this.UPDATE_ORGANISATION({
        id: this.currentOrganisation.value.id,
        data: this.formData.form
      })
        .then(async () => {
          if (this.formData.agreement) {
            await this.SET_ORGANISATION_AGREEMENT({
              id: this.currentOrganisation.value.id,
              data: this.formData.agreement
            });
            Promise.all([
              this.GET_ORGANISATION_DETAIL(this.$route.params.id),
              this.GET_ORGANISATIONS_LIST({
                direction: null,
                field: null
              }),
              this.GET_USERS_LIST({
                direction: null,
                field: null
              })
            ]).then(() => {
              this.loading = false;
            });
          }else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    changeMembersTablePage(e) {
      this.loadingMembersTable = true;
      this.GET_USERGROUP_MEMBERS({
        id: this.currentOrganisation.value.usergroup.id,
        ...this.membersTableSorted,
        page: e
      })
        .then(() => {
          this.loadingMembersTable = false;
        })
        .catch((err) => {
          console.error(err);
          this.loadingMembersTable = false;
        });
    },

    changeContactsTablePage(e) {
      this.loadingContactsTable = true;
      this.GET_CONTACTS_LIST({
        usergroupId: this.currentOrganisation.value.usergroup.id,
        page: e
      })
        .then(() => {
          this.loadingContactsTable = false;
        })
        .catch((err) => {
          console.error(err);
          this.loadingContactsTable = false;
        });
    },

    setMembersTableSorted(e) {
      this.membersTableSorted = e;
    },

    async addOrgToSphere(e) {
      if (e && e.id) {
        const index = this.selectedSpheres.findIndex(el => el.id === e.id);
        if (index === -1) {
          this.selectedSpheres.push(e);
          try {
            this.loading = true;
            await usergroupAPI.addUsergroupParent(
              this.currentUsergroup.id,
              e.id
            );
            this.loading = false;
          } catch {
            this.loading = false;
          }
        }
        this.sphere = null;
      }
    },

    async removeOrgFromSphere(e) {
      if (e && e.id) {
        const index = this.selectedSpheres.findIndex(el => el.id === e.id);
        this.selectedSpheres.splice(index, 1);
        try {
          this.loading = true;
          await usergroupAPI.removeUsergroupParent(
            this.currentUsergroup.id,
            e.id
          );
          this.loading = false;
        } catch {
          this.loading = false;
        }
      }
    }
  }
};
</script>

<style lang="less" scoped>
p {
  width: 100%;
  color: grey;
  font-style: italic;
  font-size: 0.7em;
  margin-bottom: 0.2em;
}

.contact-form-footer {
  margin: 1rem 0;
  display: flex;
  justify-content: flex-end;
  button.btn-primary {
    font-size: 1.25em;
  }
}

#orga-spheres-container {
  display: flex;
  flex-flow: row wrap;
  padding: 0.1em;
  margin: 0.5em 0;
  .orga-sphere {
    display: flex;
    margin: 0.2em 1em 0.2em 0;
    padding: 0.5em;
    border-radius: 10px;
    font-size: 0.9em;
    background-color: @blue;
    color: white;
    .b-icon {
      cursor: pointer;
    }
  }
}

</style>
